import { ACTIVE_GAME_CATALOG } from '../../../packages/domain/src/49-game-catalog.mjs';
import { createGameCards } from '../ui/34-game-cards.mjs';

// Collection labels describe gameplay, not score-recording eligibility.
const collection = (game) =>
  game.kind === 'score' || game.tags.includes('arcade') ? 'score' : 'idle';

/** Explicit Games browsing ignores ordinary-feed cadence and the gamesFeed
 * preference. Only the current actor's actual global/per-game flags grant play;
 * visiting this route never records a score or changes personalization consent. */
export function arcadeGames(state, kind = 'all') {
  if (!state?.me?.id || state.features?.games !== true) return [];
  return ACTIVE_GAME_CATALOG.filter(
    (game) =>
      state.features[`game-${game.id}`] === true && (kind === 'all' || collection(game) === kind),
  ).sort((a, b) => Number(collection(b) === 'score') - Number(collection(a) === 'score'));
}

/** Compose the same canonical playable cards used in feeds. Distant boards mount
 * only near the viewport; filtering preserves mounted rounds. Disposal releases
 * timers, pictures, gestures and observers on route/actor changes. */
export function createGames(
  ctx,
  { createCards = createGameCards, Observer = globalThis.IntersectionObserver } = {},
) {
  let disposeCurrent = () => {};
  function renderGames() {
    disposeCurrent();
    const actor = ctx.state?.me?.id;
    let disposed = false;
    let kind = 'all';
    const cards = createCards(ctx);
    const items = new Map();
    const choices = new Map();
    ctx.view.dataset.experience = 'games';
    const header = ctx.e('header', 'arcade-header');
    header.append(
      ctx.e('span', 'arcade-wordmark', ctx.uiText('PLAY SOMETHING GOOD')),
      ctx.e('h1', '', ctx.uiText('Games')),
      ctx.e('p', '', ctx.uiText('A little escape. A new high score. Your next favourite.')),
    );
    const toolbar = ctx.e('nav', 'arcade-filters');
    toolbar.setAttribute('aria-label', ctx.uiText('Game collections'));
    const count = ctx.e('span', 'arcade-count');
    count.setAttribute('role', 'status');
    const wall = ctx.e('div', 'arcade-wall');
    const empty = ctx.e('p', 'arcade-empty', ctx.uiText('No games are available here right now.'));
    const active = () => !disposed && ctx.state?.me?.id === actor && ctx.route()[0] === 'games';
    function mount(item) {
      if (!active() || item.slot.hidden || item.card) return;
      if (!arcadeGames(ctx.state).some((game) => game.id === item.game.id)) return;
      item.card = cards.render(item.game);
      item.slot.replaceChildren(item.card);
      observer?.unobserve(item.slot);
    }
    const observer = Observer
      ? new Observer(
          (entries) => {
            for (const entry of entries)
              if (entry.isIntersecting) {
                const item = items.get(entry.target.dataset.arcadeGame);
                if (item) mount(item);
              }
          },
          { rootMargin: '320px 0px', threshold: 0 },
        )
      : null;
    function refresh() {
      if (!active()) return;
      const available = new Set(arcadeGames(ctx.state, kind).map((game) => game.id));
      for (const item of items.values()) {
        item.slot.hidden = !available.has(item.game.id);
        item.card?.refreshGame?.();
        if (item.slot.hidden) observer?.unobserve(item.slot);
        else if (observer && !item.card) observer.observe(item.slot);
        else if (!observer) mount(item);
      }
      for (const [value, choice] of choices)
        choice.setAttribute('aria-pressed', String(kind === value));
      count.textContent = ctx.translate('games.available', { count: available.size });
      empty.hidden = available.size > 0;
    }
    for (const [value, label] of [
      ['all', 'All games'],
      ['score', 'Arcade'],
      ['idle', 'Unwind'],
    ]) {
      const choice = ctx.button(
        label,
        () => {
          kind = value;
          refresh();
        },
        'arcade-filter',
      );
      choices.set(value, choice);
      toolbar.append(choice);
    }
    toolbar.append(count);
    for (const game of arcadeGames(ctx.state)) {
      const slot = ctx.e('section', 'arcade-slot');
      slot.dataset.arcadeGame = game.id;
      slot.setAttribute('aria-label', game.title);
      const preview = ctx.e('div', 'arcade-preview');
      preview.append(ctx.e('h2', '', game.title), ctx.e('p', '', game.description));
      const item = { game, slot, card: null };
      preview.append(ctx.button('Play here', () => mount(item), 'secondary-button'));
      slot.append(preview);
      items.set(game.id, item);
      wall.append(slot);
    }
    ctx.view.append(header, toolbar, wall, empty);
    refresh();
    disposeCurrent = () => {
      disposed = true;
      observer?.disconnect();
      cards.dispose();
      items.clear();
    };
  }
  return {
    renderGames,
    disposeGames: () => {
      disposeCurrent();
      disposeCurrent = () => {};
    },
  };
}
